import { Component, EventEmitter, Output, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { CommonModule } from '@angular/common';
import { CommonFunctionsService } from '../../../services/common-functions.service';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { CommonDataService } from '../../../services/common-data.service';
import { Subject, takeUntil } from 'rxjs';
import { ReviewService } from '../../../services/review.service';
import { Constants } from '../../../constants/constants';
import { ReviewDetails } from '../../../models/ReviewDetails';
import { MatDialog } from '@angular/material/dialog';
import { NotesExportDialogComponent } from '../../dialog/notes-export-dialog/notes-export-dialog.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ExportObjectAsFieldValuePDFOptions } from '../../../models/ExportObjectAsFieldValuePDFOptions';
import { ExportTablesToPDFOptions } from '../../../models/ExportTableToPDF';
import { SubsAndMiscCosts } from '../../../models/SubsAndMiscCosts';
import { MultilingualCurrencyPipe } from '../../../pipes/multilingual-currency/multilingual-currency.pipe';
import { DecimalPrecisionPipe } from '../../../pipes/decimal-precision/decimal-precision.pipe';

@Component({
  selector: 'app-sol-risk',
  imports: [CommonModule, MatTableModule, TranslatePipe, DecimalPrecisionPipe, MultilingualCurrencyPipe],
  providers: [DecimalPrecisionPipe, MultilingualCurrencyPipe],
  templateUrl: './sol-risk.component.html',
  styleUrl: './sol-risk.component.scss'
})
export class SolRiskComponent {

  includeNotesInPdf: boolean = true;
  @ViewChildren(MatPaginator) paginators: QueryList<MatPaginator> | null = null;
  @ViewChild(MatSort) sort!: MatSort;
  riskDataList: any[] = [];
  dataSourceRiskWise: { [key: string]: MatTableDataSource<SubsAndMiscCosts> } = {};
  groupedReviewList: any[] = [];
  riskAppliedCost: string = '';
  percentofTDC: string = '';
  subsAndMiscCostsDetails: SubsAndMiscCosts[] = new Array<SubsAndMiscCosts>();
  checkDataExists: boolean = false;
  destroy$ = new Subject<void>();
  @Output() disablePdf = new EventEmitter<boolean>();
  constructor(private commonFunctions: CommonFunctionsService, 
    private readonly commonDataService: CommonDataService, 
    private dialog: MatDialog, 
    private readonly reviewService: ReviewService, 
    private readonly translate: TranslateService,
    private readonly multilingualCurrencyPipe: MultilingualCurrencyPipe,
    private readonly decimalPrecisionPipe: DecimalPrecisionPipe) { }

  columns: { field: string, header: string, isRightAlign: boolean, colWidth: string }[] = [
    { field: 'description', header: 'REVIEW_SOL_RISK_TAB.DESCRIPTION', isRightAlign: false, colWidth: '200px' },
    { field: 'typicalOfCountInArea', header: 'REVIEW_SOL_RISK_TAB.TYPICAL_OF_COUNT_IN_AREA', isRightAlign: true, colWidth: 'auto' },
    { field: 'miscellaneousCost', header: 'REVIEW_SOL_RISK_TAB.MISCELLANEOUS_COST', isRightAlign: true, colWidth: 'auto' },
    { field: 'notes', header: 'REVIEW_SOL_RISK_TAB.NOTES', isRightAlign: false, colWidth: 'auto' },
  ];

  displayedColumnsRiskWise: string[] = [
    'description',
    'typicalOfCountInArea',
    'miscellaneousCost',
    'notes'
  ];
  headersRiskWise: { [key: string]: string } = {};

  ngOnInit() {
    this.initializeRiskData();
    this.commonDataService.triggerTabMethod$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.exportToPdf(this.reviewService.selectedReview, Constants.SOL_RISK);
      });
    this.sendDisablePdfEvent();
  }

  sendDisablePdfEvent() {
    this.disablePdf.emit(false);
  }

  ngAfterViewInit() {
    if (this.paginators) {
      this.paginators.forEach((paginator, index) => {
        if (this.groupedReviewList[index]) {
          this.dataSourceRiskWise[this.groupedReviewList[index][0].levelID].paginator = paginator;
        }
      });
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  initializeRiskData() {
    const newCostReviewObject = this.reviewService.newCostReviewObject;
    if (newCostReviewObject && newCostReviewObject.subsAndMiscCostsDetails) {
      this.riskAppliedCost = this.commonFunctions.formatToTwoDecimal(Number(newCostReviewObject.autoRiskDollars)) ?? Constants.ZERO;
      this.percentofTDC = this.commonFunctions.formatToTwoDecimal(Number(newCostReviewObject.autoRiskPercentageOfTDC)) ?? Constants.ZERO;
      this.subsAndMiscCostsDetails = newCostReviewObject.subsAndMiscCostsDetails;
      if (this.subsAndMiscCostsDetails.length > 0) {
        this.checkDataExists = true;
        this.getAllMappedRiskWise();
        this.groupedReviewList = this.groupByLevelID(this.riskDataList);
        this.addSumsToGroups();
      }
    }
  }

  getAllMappedRiskWise() {
    const filteredData = this.subsAndMiscCostsDetails.filter(detail => detail.isSystemLevel || detail.isRiskItem);
    this.riskDataList = filteredData.filter((detail, index, array) => {
      if (detail.isSystemLevel) {
        const nextDetail = array[index + Constants.ONE];
        return nextDetail && nextDetail.isRiskItem;
      }
      return true;
    }).map(detail => {
      const indentDescription = detail.isSystemLevel;
      return this.formatDetail(detail, indentDescription);
    });

    this.populateDataSourceRiskWise();
  }

  //To-Do: get miscellaneous cost as number and remove the any
  formatDetail(detail: SubsAndMiscCosts, indentDescription: boolean): any {
    return {
      ...detail,
      description: indentDescription ? `${Constants.ADD_TAB}${detail.description.trim()}` : detail.description,
      miscellaneousCost: indentDescription ? Constants.EMPTY_STRING : this.multilingualCurrencyPipe.transform(detail.miscellaneousCost) || this.multilingualCurrencyPipe.transform(Constants.ZERO_DECIMAL) || Constants.EMPTY_STRING,
      typicalOfCountInArea: detail.typicalOfCountInArea == Constants.ZERO_STR ? Constants.EMPTY_STRING : detail.typicalOfCountInArea,
    };
  }

  populateDataSourceRiskWise() {
    this.riskDataList.forEach(detail => {
      if (!this.dataSourceRiskWise[detail.levelID]) {
        this.dataSourceRiskWise[detail.levelID] = new MatTableDataSource<SubsAndMiscCosts>();
      }
      this.dataSourceRiskWise[detail.levelID].data.push(detail);
    });
  }

  groupByLevelID(list: SubsAndMiscCosts[]): any[] {
    const groups: { [key: string]: SubsAndMiscCosts[] } = {};
    list.forEach(item => {
      if (!groups[item.levelID]) {
        groups[item.levelID] = [];
      }
      groups[item.levelID].push(item);
    });
    return Object.values(groups);
  }

  addSumsToGroups() {
    const columnsToSum = ['miscellaneousCost'];
    this.groupedReviewList.forEach(group => {
      const sum: any = { description: this.translate.instant('REVIEW_SOL_RISK_TAB.TOTAL') };
      columnsToSum.forEach(column => {
        sum[column] = this.multilingualCurrencyPipe.transform(
          this.decimalPrecisionPipe.transform(
            group.reduce((acc: any, item: any) => acc + (parseFloat(item[column].replace(Constants.DOLLAR, Constants.EMPTY_STRING)) || Constants.ZERO), Constants.ZERO)
          )
        );
      });
      group.push(sum);
    });
  }

  exportToPdf(selectedReview: ReviewDetails, tabName: string) {
    if (tabName !== Constants.SOL_RISK) return;
    if (!this.checkDataExists) {
      this.exportNoDataFound(selectedReview);
      return;
    }
    const dialogRef = this.dialog.open(NotesExportDialogComponent, {
      width: '350px',
      data: { notes: this.includeNotesInPdf },
      disableClose: true
    });

    dialogRef.afterClosed().subscribe((result: boolean | undefined) => {
      if (result === undefined) return;
      this.includeNotesInPdf = result;
      const riskSummaryTable = this.buildRiskSummaryTable();
      const tables = this.buildRiskTables();
      const options: ExportTablesToPDFOptions = {
        tables: [riskSummaryTable, ...tables],
        fileName: 'risk_report.pdf',
        hasNotes: this.includeNotesInPdf,
        estimateID: selectedReview.estimateID,
        estimateName: selectedReview.request,
        tabName: this.translate.instant('REVIEW_SOL_TABS_LABELS.RISK'),
      };
      this.commonFunctions.exportTablesToPDF(options);
    });
  }

  exportNoDataFound(selectedReview: ReviewDetails) {
    const options: ExportObjectAsFieldValuePDFOptions = {
      data: {
        [Constants.EMPTY_STRING]: this.translate.instant('REVIEW_SOL_RISK_TAB.RISK_DATA_NOT_FOUND')
      },
      title: Constants.EMPTY_STRING,
      fileName: 'risk_report.pdf',
      estimateID: selectedReview.estimateID,
      estimateName: selectedReview.request,
      tabName: this.translate.instant('REVIEW_SOL_TABS_LABELS.RISK'),
    };
    this.commonFunctions.exportObjectAsFieldValuePDF(options);
  }

  buildRiskSummaryTable() {
    return {
      title: Constants.EMPTY_STRING,
      displayedColumns: [
        this.translate.instant('REVIEW_SOL_RISK_TAB.AUTO_RISK_APPLIED'),
        this.translate.instant('REVIEW_SOL_RISK_TAB.DOLLARS'),
        this.translate.instant('REVIEW_SOL_RISK_TAB.TDC_PERCENT')
      ],
      headers: {
        [this.translate.instant('REVIEW_SOL_RISK_TAB.AUTO_RISK_APPLIED')]: this.translate.instant('REVIEW_SOL_RISK_TAB.AUTO_RISK_APPLIED'),
        [this.translate.instant('REVIEW_SOL_RISK_TAB.DOLLARS')]: this.translate.instant('REVIEW_SOL_RISK_TAB.DOLLARS'),
        [this.translate.instant('REVIEW_SOL_RISK_TAB.TDC_PERCENT')]: this.translate.instant('REVIEW_SOL_RISK_TAB.TDC_PERCENT')
      },
      data: [
        {
          [this.translate.instant('REVIEW_SOL_RISK_TAB.AUTO_RISK_APPLIED')]: this.translate.instant('REVIEW_SOL_CHECKLIST_TAB.INTRUSION_SYSTEM.RISK'),
          [this.translate.instant('REVIEW_SOL_RISK_TAB.DOLLARS')]: this.riskAppliedCost != null ? Number(this.riskAppliedCost).toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : Constants.DEFAULT_VALUE_WITH_DOLLAR,
          [this.translate.instant('REVIEW_SOL_RISK_TAB.TDC_PERCENT')]: this.percentofTDC != null && !isNaN(Number(this.percentofTDC)) ? `${this.percentofTDC}%` : Constants.ZERO_DECIMAL_PERCENT
        }
      ]
    };
  }

  buildRiskTables() {
    const displayedColumns = this.includeNotesInPdf
      ? this.displayedColumnsRiskWise
      : this.displayedColumnsRiskWise.filter(col => col.toLowerCase() !== 'notes');

    const headers = { ...this.headersRiskWise };
    if (!this.includeNotesInPdf) {
      delete headers['notes'];
    }

    return this.groupedReviewList.map((group, idx) => {
      const riskType = group[0]?.levelDescription || `Risk Group ${idx + 1}`;
      const data = this.includeNotesInPdf
        ? group
        : group.map((item: SubsAndMiscCosts) => {
          const { notes, ...rest } = item;
          return rest;
        });

      return {
        title: `${riskType}`,
        displayedColumns: displayedColumns,
        headers: headers,
        data: data,
        typeGuard: (row: any, col: string) => row[col as keyof typeof row]
      };
    });
  }
}
